/**
 * dev 환경 API 호출 로그 store.
 *
 * 모든 axios 요청/응답/에러를 ring buffer 에 기록 → DevScenarioPanel 이 구독해 화면 표시.
 * 앱(Flutter webview)에서도 동작하므로 실기기 검증 시 직접 확인 가능.
 *
 * 외부 store 패턴 (subscribe + getSnapshot) — useSyncExternalStore 와 결합.
 */

export type ApiLogKind = 'request' | 'response' | 'error'

export interface ApiLogEntry {
  id: number
  time: string
  kind: ApiLogKind
  method: string
  url: string
  status?: number
  durationMs?: number
  message?: string
  /** request body (POST/PUT/PATCH) — JSON 직렬화 */
  body?: string
  /** response body — JSON 직렬화 */
  responseBody?: string
  /** request headers 요약 */
  reqHeaders?: string
}

const MAX_LOG_COUNT = 30
let entries: ApiLogEntry[] = []
let nextId = 0
const listeners = new Set<() => void>()

function emit(): void {
  // ring buffer 자르기 — 새 배열 참조 보장 (useSyncExternalStore 가 변경 감지)
  if (entries.length > MAX_LOG_COUNT) {
    entries = entries.slice(-MAX_LOG_COUNT)
  }
  entries = [...entries]
  listeners.forEach((listener) => listener())
}

function nowTime(): string {
  return new Date().toLocaleTimeString('ko-KR', { hour12: false, fractionalSecondDigits: 3 })
}

export function pushApiLog(entry: Omit<ApiLogEntry, 'id' | 'time'>): void {
  entries.push({ id: nextId++, time: nowTime(), ...entry })
  emit()
}

export function clearApiLog(): void {
  entries = []
  emit()
}

export const subscribeApiLog = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const getApiLogSnapshot = (): ApiLogEntry[] => entries
